import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, FolderOpen, Sun, Moon, Snowflake, Keyboard } from 'lucide-react';
import axios from 'axios';
import PromptCollection from './PromptCollection';
import './Dashboard.css';
import './PromptCollection.css';

interface Stats {
  totalPrompts: number;
  totalCategories: number;
}

const PromptPage: React.FC = () => {
  const [theme, setTheme] = useState<'light' | 'dark' | 'nord'>(() => {
    const saved = localStorage.getItem('theme');
    return (saved as 'light' | 'dark' | 'nord') || 'dark';
  });

  const [stats, setStats] = useState<Stats>({ totalPrompts: 0, totalCategories: 0 });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Fetch stats for header
  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get<{ _id: string; name: string; count: number }[]>('/api/prompts/categories');
      const cats = res.data;
      setStats({
        totalCategories: cats.length,
        totalPrompts: cats.reduce((sum, c) => sum + c.count, 0),
      });
    } catch {
      // silently fail — stats are cosmetic
    }
  }, []);

  useEffect(() => {
    fetchStats();
    // Refresh stats periodically when prompts change
    const interval = setInterval(fetchStats, 15000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const cycleTheme = () => {
    const themes: Array<'light' | 'dark' | 'nord'> = ['light', 'dark', 'nord'];
    const next = themes[(themes.indexOf(theme) + 1) % themes.length];
    setTheme(next);
    localStorage.setItem('theme', next);
  };

  const themeIcon = theme === 'light' ? <Sun size={16} /> : theme === 'dark' ? <Moon size={16} /> : <Snowflake size={16} />;
  const themeLabel = theme === 'light' ? 'Light' : theme === 'dark' ? 'Dark' : 'Nord';

  return (
    <div className="pp">
      {/* Background Effects (same as Dashboard) */}
      <div className="pp-glow pp-glow--1" />
      <div className="pp-glow pp-glow--2" />
      <div className="pp-glow pp-glow--3" />
      <div className="pp-lines" />

      {/* Header */}
      <header className="pp-header">
        <div className="pp-header-left">
          <div className="pp-brand">
            <h1 className="pp-title">Promptsammlung</h1>
            <p className="pp-subtitle">Sammle, organisiere und nutze deine besten Prompts</p>
          </div>
        </div>

        <div className="pp-header-right">
          {/* Stats */}
          <div className="pp-stats">
            <div className="pp-stat">
              <BookOpen size={14} />
              <span className="pp-stat-value">{stats.totalPrompts}</span>
              <span className="pp-stat-label">Prompts</span>
            </div>
            <div className="pp-stat-divider" />
            <div className="pp-stat">
              <FolderOpen size={14} />
              <span className="pp-stat-value">{stats.totalCategories}</span>
              <span className="pp-stat-label">Kategorien</span>
            </div>
          </div>

          {/* Keyboard hint */}
          <div className="pp-kbd-hint">
            <Keyboard size={13} />
            <span><kbd>Ctrl</kbd>+<kbd>Enter</kbd> speichern</span>
          </div>

          {/* Theme Toggle */}
          <button className="pp-theme-btn" onClick={cycleTheme} title={`Theme: ${themeLabel}`}>
            {themeIcon}
            <span>{themeLabel}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="pp-body">
        <PromptCollection />
      </div>
    </div>
  );
};

export default PromptPage;
